import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Card from "components/card";
import ActiveStatusSegment from "components/ui/ActiveStatusSegment";
import DataTablePagination from "components/ui/DataTablePagination";
import DataTableRowActions from "components/list/DataTableRowActions";
import DataTableScrollContainer from "components/ui/DataTableScrollContainer";
import { DataTableEmptyState, DataTableLoadingState } from "components/ui/DataTableStates";
import SearchableFormSelect from "components/ui/SearchableFormSelect";
import Select from "components/ui/Select";
import InlineMultiSelect from "components/ui/InlineMultiSelect";
import ToggleSwitch from "components/ui/ToggleSwitch";
import { useAppLanguage } from "hooks/useAppLanguage";
import { toastError } from "lib/toast";
import { formatLocaleDateTime, toWesternDigits } from "lib/localeFormat";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  DATA_TABLE_TABLE_CLASS,
  dataTableActionsCellClass,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";
import { normalizeFormSelectOptions } from "lib/selectOptions";

type EntityRecord = Record<string, any>;
type FilterField = {
  key: string;
  labelAr: string;
  labelEn: string;
  type: "text" | "number" | "select" | "select-search" | "status-segment";
  options?: { value: string; labelAr: string; labelEn: string }[];
  /** Load options from API using `term` (backend search); debounced in the UI */
  remoteFetch?: (args: {
    term: string;
    filters: Record<string, string>;
    signal: AbortSignal;
  }) => Promise<{ value: string; labelAr: string; labelEn: string }[]>;
  /** Resolve label for current value when not in loaded page */
  resolveSelectedLabel?: (id: string) => Promise<{ labelAr: string; labelEn: string } | null>;
};

function SearchableFilterSelect({
  value,
  onChange,
  options,
  isArabic,
}: {
  value: string;
  onChange: (next: string) => void;
  options: { value: string; labelAr: string; labelEn: string }[];
  isArabic: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const label = (isArabic ? o.labelAr : o.labelEn).toLowerCase();
      return label.includes(q) || o.value.toLowerCase().includes(q);
    });
  }, [options, query, isArabic]);

  const selectedLabel = useMemo(() => {
    const opt = options.find((o) => o.value === value);
    if (!opt) return "";
    return isArabic ? opt.labelAr : opt.labelEn;
  }, [options, value, isArabic]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-start text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
      >
        <span className={`min-w-0 flex-1 truncate ${value ? "" : "text-gray-400"}`}>
          {value ? selectedLabel : isArabic ? "الكل" : "All"}
        </span>
        <span className="shrink-0 text-gray-400" aria-hidden>
          ▼
        </span>
      </button>
      {open ? (
        <div
          className={`absolute z-[130] mt-1 max-h-64 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-navy-900 ${
            isArabic ? "text-right" : "text-left"
          }`}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isArabic ? "ابحث..." : "Search..."}
            className="w-full border-b border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            autoFocus
          />
          <ul className="max-h-48 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                className={`w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 ${
                  isArabic ? "text-right" : "text-left"
                }`}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  setQuery("");
                }}
              >
                {isArabic ? "الكل" : "All"}
              </button>
            </li>
            {filtered.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-white/10 ${
                    isArabic ? "text-right" : "text-left"
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  {isArabic ? opt.labelAr : opt.labelEn}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function SearchableFilterSelectRemote({
  value,
  onChange,
  isArabic,
  filters,
  remoteFetch,
  resolveSelectedLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  isArabic: boolean;
  filters: Record<string, string>;
  remoteFetch: FilterField["remoteFetch"];
  resolveSelectedLabel?: FilterField["resolveSelectedLabel"];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [options, setOptions] = useState<{ value: string; labelAr: string; labelEn: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState<{ ar: string; en: string } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!value || !resolveSelectedLabel) {
      setResolved(null);
      return;
    }
    let cancelled = false;
    resolveSelectedLabel(value).then((l) => {
      if (!cancelled && l) setResolved({ ar: l.labelAr, en: l.labelEn });
      else if (!cancelled) setResolved(null);
    });
    return () => {
      cancelled = true;
    };
  }, [value, resolveSelectedLabel]);

  useEffect(() => {
    if (!open || !remoteFetch) return;
    const ac = new AbortController();
    setLoading(true);
    remoteFetch({ term: debouncedQuery, filters, signal: ac.signal })
      .then(setOptions)
      .catch(() => {
        if (!ac.signal.aborted) setOptions([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [open, debouncedQuery, filters, filtersKey, remoteFetch]);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const opt = options.find((o) => o.value === value);
    if (opt) return isArabic ? opt.labelAr : opt.labelEn;
    if (resolved) return isArabic ? resolved.ar : resolved.en;
    return `#${value}`;
  }, [value, options, isArabic, resolved]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => {
            if (!o) {
              setQuery("");
              setDebouncedQuery("");
            }
            return !o;
          });
        }}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-start text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
      >
        <span className={`min-w-0 flex-1 truncate ${value ? "" : "text-gray-400"}`}>
          {value ? selectedLabel : isArabic ? "الكل" : "All"}
        </span>
        <span className="shrink-0 text-gray-400" aria-hidden>
          ▼
        </span>
      </button>
      {open ? (
        <div
          className={`absolute z-[130] mt-1 max-h-64 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-navy-900 ${
            isArabic ? "text-right" : "text-left"
          }`}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isArabic ? "ابحث في السيرفر..." : "Search (server)..."}
            className="w-full border-b border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            autoFocus
          />
          {loading ? (
            <p className="px-3 py-2 text-xs text-gray-500">
              {isArabic ? "جارٍ التحميل..." : "Loading..."}
            </p>
          ) : null}
          <ul className="max-h-48 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                className={`w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 ${
                  isArabic ? "text-right" : "text-left"
                }`}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  setQuery("");
                  setDebouncedQuery("");
                }}
              >
                {isArabic ? "الكل" : "All"}
              </button>
            </li>
            {options.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-white/10 ${
                    isArabic ? "text-right" : "text-left"
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setQuery("");
                    setDebouncedQuery("");
                  }}
                >
                  {isArabic ? opt.labelAr : opt.labelEn}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

type CrudPanelProps = {
  title: string;
  description?: string;
  list: (params?: Record<string, unknown>) => Promise<{ data: EntityRecord[]; meta: any }>;
  show?: (id: number | string) => Promise<EntityRecord | null>;
  create?: (data: Record<string, unknown>) => Promise<any>;
  update?: (id: number | string, data: Record<string, unknown>) => Promise<any>;
  remove?: (id: number | string) => Promise<any>;
  block?: (id: number | string) => Promise<any>;
  unblock?: (id: number | string) => Promise<any>;
  previewKeys?: string[];
  createTemplate?: Record<string, unknown>;
  fieldOptions?: Record<string, { value: string | number; label: string }[]>;
  /** Merge dynamic options (e.g. grades filtered by stage) with static fieldOptions */
  fieldOptionsResolver?: (
    formState: Record<string, unknown>
  ) => Partial<Record<string, { value: string | number; label: string }[]>>;
  /** Per-field select placeholder (create/edit modal) */
  fieldPlaceholders?: Record<string, { ar: string; en: string }>;
  /** Per-field options loading (shows spinner instead of numeric fallback) */
  fieldOptionsLoading?: Record<string, boolean>;
  /** Shown when options finished loading but list is empty */
  fieldEmptyHints?: Record<string, { ar: string; en: string }>;
  /** Context-aware empty hints (e.g. faculty before university is chosen) */
  fieldEmptyHintsResolver?: (
    formState: Record<string, unknown>
  ) => Partial<Record<string, { ar: string; en: string }>>;
  /** Reset dependent fields when a parent select changes */
  onFormFieldChange?: (
    key: string,
    value: unknown,
    formState: Record<string, unknown>
  ) => Partial<Record<string, unknown>>;
  /** Client-side required fields before create/update */
  requiredFields?: string[];
  /** Custom form field renderers (e.g. icon picker) */
  fieldRenderers?: Record<
    string,
    (ctx: {
      value: unknown;
      onChange: (next: unknown) => void;
      formState: Record<string, unknown>;
      isArabic: boolean;
      disabled: boolean;
    }) => ReactNode
  >;
  /** Hide form fields when false (e.g. school vs university student fields) */
  fieldVisibility?: (key: string, formState: Record<string, unknown>) => boolean;
  /** Disable form fields (e.g. faculty until university is chosen) */
  fieldDisabled?: (key: string, formState: Record<string, unknown>) => boolean;
  /** `date` for date inputs; `textarea` for multiline; `email` for email */
  fieldInputTypes?: Record<string, "date" | "textarea" | "email">;
  multilineKeys?: string[];
  mapEntityToForm?: (entity: EntityRecord) => Record<string, unknown>;
  mapPayload?: (
    payload: Record<string, unknown>,
    ctx: { mode: "create" | "update" }
  ) => Record<string, unknown>;
  previewFormatters?: Record<string, (row: EntityRecord) => ReactNode>;
  enableIsActiveFilter?: boolean;
  filterFields?: FilterField[];
  sortOptions?: { value: string; labelAr: string; labelEn: string }[];
  disableAdvancedFilters?: boolean;
};

function getNestedValue(obj: unknown, path: string): unknown {
  if (!path.includes(".")) return (obj as Record<string, unknown>)?.[path];
  return path.split(".").reduce((acc: unknown, part) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[part];
  }, obj);
}

function extractValidationErrors(err: unknown): Record<string, string> {
  const response = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const rawErrors = response?.errors ?? response?.data;
  if (!rawErrors || typeof rawErrors !== "object" || Array.isArray(rawErrors)) {
    return {};
  }

  const mapped: Record<string, string> = {};
  Object.entries(rawErrors as Record<string, unknown>).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const message = value.find((item) => typeof item === "string");
      if (typeof message === "string" && message.trim()) {
        mapped[key] = message;
      }
      return;
    }
    if (typeof value === "string" && value.trim()) {
      mapped[key] = value;
    }
  });
  return mapped;
}

function isEmptyRelationId(value: unknown): boolean {
  const num = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(num) || num <= 0;
}

export default function CrudPanel({
  title,
  description,
  list,
  show,
  create,
  update,
  remove,
  block,
  unblock,
  previewKeys,
  createTemplate = {},
  fieldOptions = {},
  enableIsActiveFilter = false,
  filterFields = [],
  sortOptions = [],
  disableAdvancedFilters = false,
  fieldOptionsResolver,
  fieldPlaceholders = {},
  fieldOptionsLoading = {},
  fieldEmptyHints = {},
  fieldEmptyHintsResolver,
  onFormFieldChange,
  requiredFields = [],
  fieldRenderers = {},
  fieldVisibility,
  fieldDisabled,
  fieldInputTypes = {},
  multilineKeys = [],
  mapEntityToForm,
  mapPayload,
  previewFormatters = {},
}: CrudPanelProps) {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<EntityRecord[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [advancedFilters, setAdvancedFilters] = useState<Record<string, string>>({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [idSortDirection, setIdSortDirection] = useState<"asc" | "desc">("desc");
  const [formState, setFormState] = useState<Record<string, any>>(createTemplate);
  const [selected, setSelected] = useState<EntityRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
  const [deleteTargetId, setDeleteTargetId] = useState<number | string | null>(null);
  const [togglingRowId, setTogglingRowId] = useState<number | string | null>(null);

  const resolvedFieldOptions = useMemo(
    () => {
      const merged = {
        ...fieldOptions,
        ...(fieldOptionsResolver?.(formState) || {}),
      };
      const normalized: Record<string, { value: number | string; label: string }[]> = {};
      Object.entries(merged).forEach(([key, opts]) => {
        if (!Array.isArray(opts)) return;
        normalized[key] = normalizeFormSelectOptions(opts);
      });
      return normalized;
    },
    [fieldOptions, fieldOptionsResolver, formState]
  );

  const resolvedFieldEmptyHints = useMemo(
    () => ({
      ...fieldEmptyHints,
      ...(fieldEmptyHintsResolver?.(formState) || {}),
    }),
    [fieldEmptyHints, fieldEmptyHintsResolver, formState]
  );

  const applyFormFieldUpdate = (key: string, next: unknown) => {
    setFormErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[key];
      return nextErrors;
    });
    setFormState((prev) => {
      const patch = onFormFieldChange?.(key, next, prev) ?? {};
      return { ...prev, [key]: next, ...patch };
    });
  };

  const fieldLabel = (key: string) => {
    const mapAr: Record<string, string> = {
      name: "الاسم",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
      password: "كلمة المرور",
      password_confirmation: "تأكيد كلمة المرور",
      is_active: "نشط",
      education_type: "نوع التعليم",
      related_to: "الجهة المرتبطة",
      sort_order: "الترتيب",
      icon: "الأيقونة",
      grade_id: "الصف",
      grade_ids: "الصفوف",
      stage_id: "المرحلة الدراسية",
      stage_ids: "المراحل الدراسية",
      stage_names: "المراحل الدراسية",
      stage_label: "المرحلة",
      academic_summary: "الصف / الجامعة",
      gender: "النوع",
      parent_phone: "هاتف ولي الأمر",
      date_of_birth: "تاريخ الميلاد",
      image: "صورة",
      bio: "نبذة",
      title: "العنوان",
      body: "المحتوى",
      type: "النوع",
      permissions: "الصلاحيات",
      created_at: "تاريخ الإنشاء",
    };
    const mapEn: Record<string, string> = {
      name: "Name",
      email: "Email",
      phone: "Phone",
      password: "Password",
      password_confirmation: "Password confirmation",
      is_active: "Is active",
      education_type: "Education Type",
      related_to: "Related To",
      sort_order: "Sort order",
      icon: "Icon",
      grade_id: "Grade",
      grade_ids: "Grades",
      stage_id: "Stage",
      stage_ids: "Stages",
      stage_names: "Stages",
      stage_label: "Stage",
      academic_summary: "Grade / University",
      gender: "Gender",
      parent_phone: "Parent phone",
      date_of_birth: "Date of birth",
      image: "Image",
      bio: "Bio",
      title: "Title",
      body: "Body",
      type: "Type",
      permissions: "Permissions",
      created_at: "Created at",
    };
    const map = isArabic ? mapAr : mapEn;
    return map[key] || key.replace(/_/g, " ");
  };
  const formatCellValue = (row: EntityRecord, key: string, raw: unknown): string => {
    if (key.includes(".")) {
      const nested = getNestedValue(row, key);
      if (nested === null || nested === undefined || nested === "") return "-";
      if (typeof nested === "object" && nested !== null) {
        const o = nested as Record<string, unknown>;
        if (typeof o.name === "string") return o.name;
        return JSON.stringify(nested);
      }
      return String(nested);
    }
    if (raw === null || raw === undefined || raw === "") return "-";
    if (typeof raw === "boolean") {
      return raw ? (isArabic ? "نعم" : "Yes") : isArabic ? "لا" : "No";
    }
    if (key === "is_active") {
      const boolVal = String(raw) === "1" || String(raw).toLowerCase() === "true";
      return boolVal ? (isArabic ? "نشط" : "Active") : isArabic ? "غير نشط" : "Inactive";
    }
    if (key === "education_type" && raw === "basic") {
      return isArabic ? "تعليم أساسي" : "Basic Education";
    }
    {
      const options = resolvedFieldOptions[key];
      if (options?.length) {
        const matched = options.find((opt) => String(opt.value) === String(raw));
        if (matched) return matched.label;
      }
    }
    if (key.includes("_at")) {
      const date = new Date(String(raw));
      if (!Number.isNaN(date.getTime())) {
        return formatLocaleDateTime(date, isArabic);
      }
    }
    // Render relational names for supported foreign keys when a nested relation exists.
    if (key.endsWith("_id")) {
      if (raw === 0 || raw === "0") {
        return isArabic ? "غير محدد" : "Not selected";
      }
      const options = resolvedFieldOptions[key];
      if (options?.length) {
        const matched = options.find((opt) => String(opt.value) === String(raw));
        if (matched) return matched.label;
      }
      const relationKey = key.replace(/_id$/, "");
      const relation = row?.[relationKey];
      if (relation && typeof relation === "object") {
        const relationObj = relation as Record<string, unknown>;
        if (typeof relationObj.name === "string" && relationObj.name.trim()) {
          return relationObj.name;
        }
        if (typeof relationObj.title === "string" && relationObj.title.trim()) {
          return relationObj.title;
        }
      }
      if (raw !== null && raw !== undefined && raw !== "") {
        return isArabic ? "غير محدد" : "Not selected";
      }
    }
    if (typeof raw === "object") {
      const obj = raw as Record<string, unknown>;
      if (typeof obj.name === "string") return obj.name;
      if (typeof obj.title === "string") return obj.title;
      return JSON.stringify(obj);
    }
    return toWesternDigits(String(raw));
  };
  const toBoolean = (value: unknown): boolean =>
    String(value) === "1" || String(value).toLowerCase() === "true";

  const fieldPlaceholder = (key: string) => {
    const custom = fieldPlaceholders[key];
    if (custom) return isArabic ? custom.ar : custom.en;
    return isArabic ? "اختر..." : "Select...";
  };

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    requiredFields.forEach((key) => {
      if (fieldVisibility && !fieldVisibility(key, formState)) {
        return;
      }
      const raw = formState[key];
      if (key === "name") {
        if (!String(raw ?? "").trim()) {
          errors[key] = isArabic ? "الاسم مطلوب" : "Name is required";
        }
        return;
      }
      if (key.endsWith("_ids")) {
        const list = Array.isArray(raw) ? raw : [];
        if (list.length === 0) {
          errors[key] = isArabic
            ? `${fieldLabel(key)} مطلوب`
            : `${fieldLabel(key)} is required`;
        }
        return;
      }
      if (key.endsWith("_id")) {
        if (isEmptyRelationId(raw)) {
          errors[key] = isArabic
            ? `${fieldLabel(key)} مطلوب`
            : `${fieldLabel(key)} is required`;
          return;
        }
        const options = resolvedFieldOptions[key];
        if (options?.length) {
          const isValid = options.some((opt) => Number(opt.value) === Number(raw));
          if (!isValid) {
            errors[key] = isArabic
              ? "يجب اختيار قيمة صحيحة"
              : "Please select a valid value";
          }
        }
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isSubmitDisabled = useMemo(() => {
    for (const key of requiredFields) {
      if (fieldVisibility && !fieldVisibility(key, formState)) {
        continue;
      }
      if (fieldOptionsLoading[key]) return true;
      const hint = resolvedFieldEmptyHints[key];
      const options = resolvedFieldOptions[key];
      if (hint && (!options || options.length === 0) && !fieldOptionsLoading[key]) {
        return true;
      }
      if (key.endsWith("_ids")) {
        const list = Array.isArray(formState[key]) ? formState[key] : [];
        if (list.length === 0) return true;
      }
    }
    return false;
  }, [requiredFields, fieldOptionsLoading, resolvedFieldEmptyHints, resolvedFieldOptions, fieldVisibility, formState]);

  const keysToShow = useMemo(() => {
    if (previewKeys && previewKeys.length) return previewKeys;
    const first = items[0];
    if (!first) return ["id", "name"];
    return Object.keys(first).slice(0, 6);
  }, [items, previewKeys]);

  const mergedFilterFields = useMemo(() => {
    const fields = [...filterFields];
    const hasIsActive = fields.some((f) => f.key === "filter[is_active]");
    if (enableIsActiveFilter && !hasIsActive) {
      fields.push({
        key: "filter[is_active]",
        labelAr: "الحالة",
        labelEn: "Status",
        type: "status-segment",
      });
    }
    return fields;
  }, [enableIsActiveFilter, filterFields]);

  const fetchList = async (
    targetPage = page,
    term = searchTerm,
    extraFilters: Record<string, string> = advancedFilters
  ) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {
        page: targetPage,
        limit,
        term: term || undefined,
        sort_by: sortBy || undefined,
        sort_direction: sortDirection || undefined,
      };
      Object.entries(extraFilters).forEach(([key, value]) => {
        if (value !== "") params[key] = value;
      });
      const result = await list({
        ...params,
      });
      setItems(Array.isArray(result?.data) ? result.data : []);
      setMeta(result?.meta || null);
      setPage(targetPage);
    } catch (err: any) {
      toastError(
        err?.response?.data?.message || (isArabic ? "فشل تحميل البيانات" : "Failed to load data")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, sortBy, sortDirection]);

  const formKeys = useMemo(
    () => Object.keys(createTemplate || {}).filter((k) => k !== "id"),
    [createTemplate]
  );
  const visibleFormKeys = useMemo(
    () => formKeys.filter((k) => fieldVisibility?.(k, formState) ?? true),
    [formKeys, fieldVisibility, formState]
  );
  const totalPages = Math.max(1, Number(meta?.last_page || 1));
  const sortedItems = useMemo(() => {
    const cloned = [...items];
    cloned.sort((a, b) => {
      const aId = Number(a?.id ?? 0);
      const bId = Number(b?.id ?? 0);
      return idSortDirection === "asc" ? aId - bId : bId - aId;
    });
    return cloned;
  }, [items, idSortDirection]);
  const buildSubmitPayload = (mode: "create" | "update"): Record<string, unknown> => {
    const keys = Object.keys(createTemplate).filter((key) => {
      if (key === "id") return false;
      if (fieldVisibility && !fieldVisibility(key, formState)) return false;
      return true;
    });

    let normalizedPayload: Record<string, unknown> = {};
    keys.forEach((key) => {
      normalizedPayload[key] = formState[key];
    });

    Object.entries(createTemplate).forEach(([key, templateValue]) => {
      if (typeof templateValue === "boolean") {
        normalizedPayload[key] = toBoolean(normalizedPayload[key] ?? templateValue);
      }
      if (key.endsWith("_ids") && Array.isArray(normalizedPayload[key])) {
        normalizedPayload[key] = (normalizedPayload[key] as unknown[])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0);
      }
      if (key.endsWith("_id") && !key.endsWith("_ids") && !isEmptyRelationId(normalizedPayload[key])) {
        normalizedPayload[key] = Number(normalizedPayload[key]);
      }
    });

    if (mapPayload) {
      normalizedPayload = mapPayload(normalizedPayload, { mode });
    }

    return normalizedPayload;
  };

  const applySubmitError = (err: unknown, fallbackMessage: string) => {
    const fieldErrors = extractValidationErrors(err);
    if (Object.keys(fieldErrors).length > 0) {
      setFormErrors(fieldErrors);
      return;
    }
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 422) {
      return;
    }
    toastError(
      (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
        ?.message ||
        (err as { message?: string })?.message ||
        fallbackMessage
    );
  };

  const handleCreate = async () => {
    if (!create) return;
    if (!validateForm()) return;
    try {
      await create(buildSubmitPayload("create"));
      await fetchList(1);
      setFormState(createTemplate);
      setFormErrors({});
      setIsModalOpen(false);
    } catch (err: unknown) {
      applySubmitError(err, isArabic ? "فشل الإنشاء" : "Create failed");
    }
  };

  const handleView = async (id: number | string) => {
    try {
      if (show) {
        const entity = await show(id);
        setSelected(entity || null);
        if (entity && mapEntityToForm) {
          setFormState({ ...createTemplate, ...mapEntityToForm(entity as EntityRecord) });
        } else {
          setFormState(entity || {});
        }
        setModalMode("view");
        setIsModalOpen(true);
      } else {
        const existing = items.find((item) => item.id === id) || null;
        setSelected(existing);
        setFormState(existing || {});
        setModalMode("view");
        setIsModalOpen(true);
      }
    } catch (err: any) {
      toastError(
        err?.response?.data?.message || (isArabic ? "فشل عرض البيانات" : "Show failed")
      );
    }
  };

  const handleEdit = async (id: number | string) => {
    try {
      if (show) {
        const entity = await show(id);
        setSelected(entity || null);
        if (entity && mapEntityToForm) {
          setFormState({ ...createTemplate, ...mapEntityToForm(entity as EntityRecord) });
        } else {
          setFormState(entity || {});
        }
        setModalMode("edit");
        setIsModalOpen(true);
      } else {
        const existing = items.find((item) => item.id === id) || null;
        setSelected(existing);
        setFormState(existing || {});
        setModalMode("edit");
        setIsModalOpen(true);
      }
    } catch (err: any) {
      toastError(
        err?.response?.data?.message ||
          (isArabic ? "فشل تحميل بيانات التعديل" : "Edit load failed")
      );
    }
  };

  const handleUpdate = async () => {
    if (!update || !selected?.id) return;
    if (!validateForm()) return;
    try {
      await update(selected.id, buildSubmitPayload("update"));
      await fetchList(page);
      setFormErrors({});
      setIsModalOpen(false);
    } catch (err: unknown) {
      applySubmitError(err, isArabic ? "فشل التحديث" : "Update failed");
    }
  };

  const handleDelete = async (id: number | string): Promise<boolean> => {
    if (!remove) return false;
    try {
      await remove(id);
      await fetchList(page);
      return true;
    } catch (err: any) {
      toastError(
        err?.response?.data?.message || (isArabic ? "فشل الحذف" : "Delete failed")
      );
      return false;
    }
  };

  const handleBlockToggle = async (row: EntityRecord) => {
    if (!row?.id || !block || !unblock) return;
    try {
      if (row.is_active) {
        await block(row.id);
      } else {
        await unblock(row.id);
      }
      await fetchList(page);
    } catch (err: any) {
      toastError(
        err?.response?.data?.message ||
          (isArabic ? "فشل تغيير الحالة" : "Status action failed")
      );
    }
  };

  const handleRowActiveToggle = async (row: EntityRecord) => {
    if (!row?.id) return;
    const id = row.id;
    if (block && unblock) {
      setTogglingRowId(id);
      try {
        await handleBlockToggle(row);
      } finally {
        setTogglingRowId(null);
      }
      return;
    }
    if (!update) return;
    const next = !toBoolean(row.is_active);
    setTogglingRowId(id);
    try {
      await update(id, { is_active: next });
      await fetchList(page);
    } catch (err: any) {
      toastError(
        err?.response?.data?.message ||
          err?.message ||
          (isArabic ? "فشل تغيير الحالة" : "Status update failed")
      );
    } finally {
      setTogglingRowId(null);
    }
  };

  const totalItems = Number(meta?.total || items.length || 0);
  const perPage = Math.max(1, Number(meta?.per_page || limit) || 10);
  const fromItem =
    totalItems === 0 ? 0 : Number(meta?.from ?? (page - 1) * perPage + 1);
  const toItem =
    totalItems === 0 ? 0 : Number(meta?.to ?? Math.min(page * perPage, totalItems));

  return (
    <Card extra="w-full p-6">
      <div className={`mb-4 flex items-center justify-between ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className={isArabic ? "text-right" : "text-left"}>
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">{title}</h3>
          {description ? <p className="text-sm text-gray-500">{description}</p> : null}
        </div>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <div
            className={`flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 py-1 dark:border-white/10 dark:bg-navy-900 ${
              isArabic ? "flex-row-reverse" : ""
            }`}
          >
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isArabic ? "بحث..." : "Search..."}
              className={`w-36 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-500 md:w-48 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder:text-gray-300 ${
                isArabic ? "text-right" : "text-left"
              }`}
            />
            <button
              type="button"
              onClick={() => fetchList(1, searchTerm)}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
            >
              {isArabic ? "بحث" : "Search"}
            </button>
            {searchTerm.trim() ? (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  fetchList(1, "");
                }}
                className="rounded-lg bg-gray-200 px-2 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-white"
                title={isArabic ? "مسح البحث" : "Clear search"}
              >
                ×
              </button>
            ) : null}
          </div>
          {!disableAdvancedFilters ? (
            <button
              type="button"
              onClick={() => setIsFiltersOpen(true)}
              className="rounded-lg bg-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-white"
            >
              {isArabic ? "فلاتر متقدمة" : "Advanced Filters"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => fetchList(page)}
            className="rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white"
          >
            {isArabic ? "تحديث" : "Refresh"}
          </button>
        </div>
      </div>
      {(create || update) && (
        <div className="mb-3 flex gap-2">
          {create ? (
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setFormState(createTemplate);
                setFormErrors({});
                setModalMode("create");
                setIsModalOpen(true);
              }}
              className="rounded-lg bg-green-600 px-4 py-2 text-xs font-semibold text-white"
            >
              {isArabic ? "إضافة جديد" : "Add New"}
            </button>
          ) : null}
        </div>
      )}

      <DataTableScrollContainer className="mb-4">
        {loading ? (
          <DataTableLoadingState
            message={isArabic ? "جارٍ تحميل البيانات..." : "Loading data..."}
          />
        ) : sortedItems.length === 0 ? (
          <DataTableEmptyState
            title={isArabic ? "لا توجد بيانات لعرضها" : "No data to display"}
            hint={
              isArabic
                ? "جرّب تعديل البحث أو إضافة سجل جديد."
                : "Try changing search filters or add a new record."
            }
          />
        ) : (
          <table className={`${DATA_TABLE_TABLE_CLASS} min-w-[920px]`} dir={isArabic ? "rtl" : "ltr"}>
            <thead>
              <tr className={DATA_TABLE_HEADER_ROW}>
                {keysToShow.map((key) => (
                  <th
                    key={key}
                    className={dataTableHeaderCellClass(isArabic, false)}
                  >
                    {key === "id" ? (
                      <button
                        type="button"
                        onClick={() =>
                          setIdSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
                        }
                        className="inline-flex items-center gap-1 font-semibold text-gray-600 hover:text-brand-500 dark:text-gray-200"
                        title={
                          isArabic
                            ? "تغيير ترتيب المعرف"
                            : "Toggle ID sort direction"
                        }
                      >
                        {fieldLabel(key)}
                        <span className="text-[10px]">
                          {idSortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      </button>
                    ) : (
                      fieldLabel(key)
                    )}
                  </th>
                ))}
                <th className={dataTableHeaderCellClass(isArabic, false, { actions: true })}>
                  {isArabic ? "الإجراءات" : "Actions"}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((row) => (
                <tr className={DATA_TABLE_BODY_ROW} key={row.id}>
                  {keysToShow.map((key) => (
                    <td key={key} className={dataTableBodyCellClass(isArabic)}>
                      {previewFormatters[key] ? (
                        <span className="text-sm">{previewFormatters[key](row)}</span>
                      ) : key === "is_active" ? (
                        <ToggleSwitch
                          checked={toBoolean(row[key])}
                          disabled={
                            togglingRowId === row.id ||
                            (!update && !(block && unblock))
                          }
                          onChange={() => handleRowActiveToggle(row)}
                          aria-label={fieldLabel("is_active")}
                        />
                      ) : (
                        formatCellValue(row, key, row[key])
                      )}
                    </td>
                  ))}
                  <td className={dataTableActionsCellClass(isArabic)}>
                    <DataTableRowActions
                      isArabic={isArabic}
                      actions={[
                        {
                          id: "view",
                          label: isArabic ? "عرض" : "View",
                          variant: "default",
                          onClick: () => handleView(row.id),
                        },
                        ...(update
                          ? [
                              {
                                id: "edit",
                                label: isArabic ? "تعديل" : "Edit",
                                variant: "warning" as const,
                                onClick: () => handleEdit(row.id),
                              },
                            ]
                          : []),
                        ...(remove
                          ? [
                              {
                                id: "delete",
                                label: isArabic ? "حذف" : "Delete",
                                variant: "danger" as const,
                                onClick: () => setDeleteTargetId(row.id),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableScrollContainer>

      <DataTablePagination
        isArabic={isArabic}
        page={page}
        totalPages={totalPages}
        fromItem={fromItem}
        toItem={toItem}
        totalItems={totalItems}
        onPageChange={(p) => fetchList(p)}
        className="mb-4"
      />

      {isModalOpen && (create || update)
        ? createPortal(
            <div
              className="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-[3px]"
              onClick={() => {
                setIsModalOpen(false);
                setSelected(null);
                setFormState(createTemplate);
                setModalMode("create");
              }}
            >
              <div
                className="apple-modal my-auto flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl dark:bg-navy-800"
                onClick={(e) => e.stopPropagation()}
                dir={isArabic ? "rtl" : "ltr"}
                role="dialog"
                aria-modal="true"
              >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 pb-4 pt-5 dark:border-white/10 sm:px-7 sm:pt-7">
              <div>
                <h4 className="text-xl font-extrabold text-navy-700 dark:text-white">
                  {modalMode === "view"
                    ? isArabic
                      ? "عرض التفاصيل"
                      : "View Details"
                    : selected?.id
                    ? isArabic
                      ? "تعديل البيانات"
                      : "Edit Data"
                    : isArabic
                    ? "إضافة بيانات جديدة"
                    : "Add New Data"}
                </h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {selected?.id
                    ? isArabic
                      ? `رقم السجل: #${selected.id}`
                      : `Record ID: #${selected.id}`
                    : isArabic
                    ? "أدخل البيانات المطلوبة ثم احفظ."
                    : "Fill required fields then save."}
                </p>
              </div>
              <button
                type="button"
                aria-label={isArabic ? "إغلاق" : "Close"}
                className="rounded-xl bg-gray-100 px-3 py-2 text-xl leading-none text-gray-600 transition hover:bg-gray-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelected(null);
                  setFormState(createTemplate);
                  setModalMode("create");
                }}
              >
                ×
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-7">
            {modalMode === "view" ? (
              <div className="grid gap-3 md:grid-cols-2">
                {keysToShow.map((key) => {
                  const viewRow = (selected || formState) as EntityRecord;
                  return (
                    <div
                      key={key}
                      className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-white/10 dark:bg-navy-900"
                    >
                      <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                      <p className="text-sm font-semibold text-navy-700 dark:text-white">
                        {previewFormatters[key]
                          ? previewFormatters[key](viewRow)
                          : formatCellValue(viewRow, key, viewRow?.[key])}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {visibleFormKeys.map((key) => {
                const value = formState[key];
                const templateValue = (createTemplate as any)?.[key];
                const isFieldDisabled = fieldDisabled?.(key, formState) ?? false;
                const customRenderer = fieldRenderers[key];
                if (customRenderer) {
                  return (
                    <div key={key} className="md:col-span-2">
                      <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                      {customRenderer({
                        value,
                        onChange: (next) => applyFormFieldUpdate(key, next),
                        formState,
                        isArabic,
                        disabled: isFieldDisabled,
                      })}
                    </div>
                  );
                }
                if (typeof templateValue === "boolean") {
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-3 text-sm dark:bg-navy-900"
                    >
                      <span className="font-semibold text-gray-700 dark:text-gray-200">
                        {fieldLabel(key)}
                      </span>
                      <ToggleSwitch
                        checked={Boolean(value)}
                        onChange={(next) =>
                          setFormState((prev) => ({ ...prev, [key]: next }))
                        }
                        aria-label={fieldLabel(key)}
                      />
                    </div>
                  );
                }

                if (typeof templateValue === "string" && resolvedFieldOptions[key]?.length) {
                  const strOptions = resolvedFieldOptions[key]!;
                  return (
                    <div key={key}>
                      <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <SearchableFormSelect
                          value={(value as string) ?? ""}
                          onChange={(next) => applyFormFieldUpdate(key, next)}
                          options={strOptions}
                          isArabic={isArabic}
                          placeholder={fieldPlaceholder(key)}
                        />
                    </div>
                  );
                }

                if (
                  typeof templateValue === "number" ||
                  (templateValue == null && key.endsWith("_id"))
                ) {
                  const options = resolvedFieldOptions[key];
                  const loading = Boolean(fieldOptionsLoading[key]);
                  const emptyHint = resolvedFieldEmptyHints[key];
                  const isRequired = requiredFields.includes(key);

                  if (loading) {
                    return (
                      <div key={key}>
                        <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <p className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-500 dark:border-white/10 dark:bg-navy-900">
                          {isArabic ? "جارٍ التحميل..." : "Loading..."}
                        </p>
                      </div>
                    );
                  }

                  if (emptyHint && (!options || options.length === 0)) {
                    return (
                      <div key={key}>
                        <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <SearchableFormSelect
                          value={null}
                          onChange={() => {}}
                          options={[]}
                          isArabic={isArabic}
                          numeric
                          allowEmpty={false}
                          disabled
                          placeholder={isArabic ? emptyHint.ar : emptyHint.en}
                        />
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          {isArabic ? emptyHint.ar : emptyHint.en}
                        </p>
                      </div>
                    );
                  }

                  if (options && options.length > 0) {
                    return (
                      <div key={key}>
                        <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <SearchableFormSelect
                          value={
                            isEmptyRelationId(value)
                              ? null
                              : Number(value)
                          }
                          onChange={(next) => applyFormFieldUpdate(key, next)}
                          options={options}
                          isArabic={isArabic}
                          numeric
                          allowEmpty={!isRequired}
                          placeholder={fieldPlaceholder(key)}
                          disabled={isFieldDisabled}
                        />
                        {formErrors[key] ? (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors[key]}</p>
                        ) : null}
                      </div>
                    );
                  }
                  return (
                    <div key={key}>
                      <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                      <input
                        type="number"
                        value={value ?? ""}
                        onChange={(e) =>
                          setFormState((prev) => ({ ...prev, [key]: Number(e.target.value) }))
                        }
                        placeholder={fieldLabel(key)}
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                      />
                      {formErrors[key] ? (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors[key]}</p>
                      ) : null}
                    </div>
                  );
                }

                if (Array.isArray(templateValue)) {
                  const multiOptions = resolvedFieldOptions[key] ?? [];
                  const loading = Boolean(fieldOptionsLoading[key]);
                  const emptyHint = resolvedFieldEmptyHints[key];
                  const selectedValues = Array.isArray(value)
                    ? value.map((item) =>
                        typeof item === "number" || typeof item === "string" ? item : Number(item)
                      )
                    : [];

                  if (loading) {
                    return (
                      <div key={key} className="md:col-span-2">
                        <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <p className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-500 dark:border-white/10 dark:bg-navy-900">
                          {isArabic ? "جارٍ التحميل..." : "Loading..."}
                        </p>
                      </div>
                    );
                  }

                  if (emptyHint && multiOptions.length === 0) {
                    return (
                      <div key={key} className="md:col-span-2">
                        <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-xs text-amber-700 dark:text-amber-300">
                          {isArabic ? emptyHint.ar : emptyHint.en}
                        </div>
                      </div>
                    );
                  }

                  if (multiOptions.length > 0 || key.endsWith("_ids")) {
                    return (
                      <div key={key} className="md:col-span-2">
                        <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                        <InlineMultiSelect
                          value={selectedValues}
                          showSelectAll={key === "stage_ids" || key === "grade_ids"}
                          selectionSummary={key === "stage_ids"
                            ? isArabic
                              ? `تم اختيار ${selectedValues.length} مرحلة`
                              : `${selectedValues.length} stage(s) selected`
                            : undefined}
                          options={multiOptions}
                          onChange={(next) =>
                            applyFormFieldUpdate(
                              key,
                              next.map((item) => Number(item)).filter((id) => Number.isFinite(id) && id > 0)
                            )
                          }
                          isArabic={isArabic}
                          placeholder={fieldPlaceholder(key)}
                          disabled={isFieldDisabled}
                        />
                        {formErrors[key] ? (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors[key]}</p>
                        ) : null}
                      </div>
                    );
                  }

                  return (
                    <div key={key}>
                      <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                      <input
                        type="text"
                        value={Array.isArray(value) ? value.join(", ") : ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            [key]: e.target.value
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                          }))
                        }
                        placeholder={`${fieldLabel(key)} (,)`}
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                      />
                    </div>
                  );
                }

                const inputKind = fieldInputTypes[key];
                if (inputKind === "textarea" || multilineKeys.includes(key)) {
                  return (
                    <div key={key} className="md:col-span-2">
                      <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                      <textarea
                        value={value ?? ""}
                        onChange={(e) => setFormState((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder={fieldLabel(key)}
                        rows={4}
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                      />
                    </div>
                  );
                }

                const isDate = inputKind === "date";
                return (
                  <div key={key}>
                    <p className="mb-1 text-xs font-semibold text-gray-500">{fieldLabel(key)}</p>
                    <div className={isDate ? "ef-date-field" : undefined}>
                      <input
                        type={
                          String(key).toLowerCase().includes("password")
                            ? "password"
                            : inputKind === "date"
                            ? "date"
                            : inputKind === "email"
                            ? "email"
                            : "text"
                        }
                        value={value ?? ""}
                        onChange={(e) => applyFormFieldUpdate(key, e.target.value)}
                        placeholder={fieldLabel(key)}
                        className={`ef-input w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none transition-shadow focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-900 dark:text-white ${
                          isDate ? "ef-date-input" : ""
                        }`}
                      />
                    </div>
                    {formErrors[key] ? (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors[key]}</p>
                    ) : null}
                  </div>
                );
              })}
              </div>
            )}
            </div>
            <div className={`flex shrink-0 gap-2 border-t border-gray-200 px-5 pb-5 pt-4 dark:border-white/10 sm:px-7 sm:pb-7 ${isArabic ? "flex-row-reverse" : ""}`}>
              {modalMode === "create" && !selected?.id && create ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={isSubmitDisabled}
                  className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isArabic ? "إنشاء" : "Create"}
                </button>
              ) : null}
              {modalMode === "edit" && selected?.id && update ? (
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={isSubmitDisabled}
                  className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isArabic ? "حفظ التعديلات" : "Save changes"}
                </button>
              ) : null}
              {modalMode === "view" && selected?.id && update ? (
                <button
                  type="button"
                  onClick={() => setModalMode("edit")}
                  className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600"
                >
                  {isArabic ? "تعديل" : "Edit"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelected(null);
                  setFormState(createTemplate);
                  setModalMode("create");
                }}
                className="rounded-xl bg-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
              >
                {isArabic ? "إغلاق" : "Close"}
              </button>
            </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {deleteTargetId !== null
        ? createPortal(
            <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[3px]">
              <div className="w-full max-w-md rounded-[24px] bg-white p-5 shadow-2xl dark:bg-navy-800">
                <div className="mb-3">
                  <h4 className="text-lg font-bold text-red-600 dark:text-red-400">
                    {isArabic ? "تأكيد الحذف" : "Confirm deletion"}
                  </h4>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-200">
                    {isArabic
                      ? "سيتم حذف هذا السجل نهائيًا ولا يمكن التراجع عن العملية."
                      : "This record will be permanently deleted and this action cannot be undone."}
                  </p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-200 pt-4 dark:border-white/10">
                  <button
                    type="button"
                    onClick={async () => {
                      const deleted = await handleDelete(deleteTargetId);
                      if (deleted) {
                        setDeleteTargetId(null);
                      }
                    }}
                    className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    {isArabic ? "نعم، حذف" : "Yes, delete"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTargetId(null)}
                    className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-white"
                  >
                    {isArabic ? "إلغاء" : "Cancel"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {isFiltersOpen && !disableAdvancedFilters
        ? createPortal(
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-[2px]"
          onClick={() => setIsFiltersOpen(false)}
        >
          <div
            className="my-auto flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 pb-3 pt-5 dark:border-white/10">
              <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                {isArabic ? "الفلاتر المتقدمة" : "Advanced Filters"}
              </h4>
              <button
                type="button"
                onClick={() => setIsFiltersOpen(false)}
                className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-semibold text-gray-500">{isArabic ? "البحث" : "Search"}</p>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-gray-500">{isArabic ? "عدد العناصر" : "Limit"}</p>
                <input
                  type="number"
                  min={1}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value) || 10)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-gray-500">{isArabic ? "الترتيب حسب" : "Sort by"}</p>
                <Select
                  value={sortBy || null}
                  onChange={(next) => setSortBy(next === null ? "" : String(next))}
                  options={sortOptions.map((opt) => ({
                    value: opt.value,
                    label: isArabic ? opt.labelAr : opt.labelEn,
                  }))}
                  isArabic={isArabic}
                  emptyValue=""
                  placeholder={isArabic ? "افتراضي" : "Default"}
                />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-gray-500">{isArabic ? "اتجاه الترتيب" : "Sort direction"}</p>
                <Select
                  value={sortDirection}
                  onChange={(next) => setSortDirection(String(next) as "asc" | "desc")}
                  options={[
                    { value: "desc", label: isArabic ? "تنازلي" : "Descending" },
                    { value: "asc", label: isArabic ? "تصاعدي" : "Ascending" },
                  ]}
                  isArabic={isArabic}
                  allowEmpty={false}
                  clearable={false}
                />
              </div>
              {mergedFilterFields.map((field) => (
                <div key={field.key}>
                  <p className="mb-1 text-xs font-semibold text-gray-500">
                    {isArabic ? field.labelAr : field.labelEn}
                  </p>
                  {field.type === "status-segment" ? (
                    <ActiveStatusSegment
                      value={(advancedFilters[field.key] ?? "") as "" | "1" | "0"}
                      onChange={(next) =>
                        setAdvancedFilters((prev) => ({ ...prev, [field.key]: next }))
                      }
                      isArabic={isArabic}
                      className="w-full justify-between sm:w-auto"
                    />
                  ) : field.type === "select" ? (
                    <Select
                      value={advancedFilters[field.key] || null}
                      onChange={(next) =>
                        setAdvancedFilters((prev) => ({
                          ...prev,
                          [field.key]: next === null ? "" : String(next),
                        }))
                      }
                      options={(field.options || []).map((opt) => ({
                        value: opt.value,
                        label: isArabic ? opt.labelAr : opt.labelEn,
                      }))}
                      isArabic={isArabic}
                      emptyValue=""
                      placeholder={isArabic ? "الكل" : "All"}
                    />
                  ) : field.type === "select-search" && field.remoteFetch ? (
                    <SearchableFilterSelectRemote
                      value={advancedFilters[field.key] ?? ""}
                      onChange={(next) =>
                        setAdvancedFilters((prev) => ({ ...prev, [field.key]: next }))
                      }
                      filters={advancedFilters}
                      remoteFetch={field.remoteFetch}
                      resolveSelectedLabel={field.resolveSelectedLabel}
                      isArabic={isArabic}
                    />
                  ) : field.type === "select-search" ? (
                    <SearchableFilterSelect
                      value={advancedFilters[field.key] ?? ""}
                      onChange={(next) =>
                        setAdvancedFilters((prev) => ({ ...prev, [field.key]: next }))
                      }
                      options={field.options || []}
                      isArabic={isArabic}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={advancedFilters[field.key] ?? ""}
                      onChange={(e) =>
                        setAdvancedFilters((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                    />
                  )}
                </div>
              ))}
            </div>
            </div>
            <div className="flex shrink-0 gap-2 border-t border-gray-200 px-5 pb-5 pt-4 dark:border-white/10">
              <button
                type="button"
                onClick={() => {
                  fetchList(1, searchTerm, advancedFilters);
                  setIsFiltersOpen(false);
                }}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white"
              >
                {isArabic ? "تطبيق" : "Apply"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setLimit(10);
                  setSortBy("");
                  setSortDirection("desc");
                  setAdvancedFilters({});
                  fetchList(1, "", {});
                }}
                className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
              >
                {isArabic ? "إعادة تعيين" : "Reset"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
        : null}
    </Card>
  );
}
